import React, { useState, useEffect } from 'react';
import formatPhoneNumber from "../../components/phoneUtils";
import ConfirmationDialog from '../utils/ConfirmationDialog';
import { getInitials } from '../../utils/helpers';
import { getFirstEmail, getAllEmails } from '../../utils/emailHelpers';


const IndividualsGrid = ({ 
  individuals, 
  selectedIndividuals, 
  onIndividualSelect, 
  onIndividualClick, 
  onIndividualDelete,
  getTagStyle,
  selectAll,
  onSelectAll,
}) => {

  // État pour détecter si une touche modificatrice est maintenue
  const [isModifierPressed, setIsModifierPressed] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [individualToDelete, setIndividualToDelete] = useState(null);

  // Écouter les événements de touches au niveau global
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey) {
        setIsModifierPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (!e.metaKey && !e.ctrlKey) {
        setIsModifierPressed(false);
      }
    };

    // Gérer la perte de focus de la fenêtre
    const handleBlur = () => {
      setIsModifierPressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Fonction pour gérer la suppression avec confirmation
  const handleDelete = (e, individualId) => {
    e.stopPropagation(); // Empêcher le clic sur la carte
    setIndividualToDelete(individualId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (individualToDelete) {
      onIndividualDelete(individualToDelete);
      setShowDeleteDialog(false);
      setIndividualToDelete(null);
    }
  };

  // Fonction pour gérer la sélection
  const handleSelect = (e, individualId) => {
    e.stopPropagation(); // Empêcher le clic sur la carte
    onIndividualSelect(individualId);
  };

  // Fonction pour gérer les clics sur la carte avec touches modificatrices
  const handleCardClick = (e, individualId) => {
    // Détecter les touches modificatrices (Cmd sur Mac, Ctrl sur Windows/Linux)
    const isModifierPressed = e.metaKey || e.ctrlKey;
    
    if (isModifierPressed) {
      // Avec modificateur : sélectionner/désélectionner
      e.preventDefault(); // Empêcher la navigation
      onIndividualSelect(individualId);
    } else {
      // Sans modificateur : navigation normale
      onIndividualClick(individualId);
    }
  };

  // Vérification de sécurité : si individuals est undefined ou null, le traiter comme un tableau vide
  if (!individuals || !Array.isArray(individuals) || individuals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg px-3 mx-8">
        <i className="bi bi-people text-6xl text-slate-300 mb-2"></i>
        <h3 className="text-xl font-medium text-slate-600 mb-2">Aucun contact</h3>
      </div>
    );
  }

  return (
    <div>
      {/* Aide discrète quand pas en mode sélection */}
        {/*
        <div className="mb-2 text-center">
          <span className="text-xs text-slate-500">
            💡 Maintenez <kbd className="px-1 py-0.5 bg-slate-200 rounded text-xs">Cmd</kbd> ou <kbd className="px-1 py-0.5 bg-slate-200 rounded text-xs">Ctrl</kbd> et cliquez pour sélectionner rapidement
          </span>
        </div>
        */}

    <div className="px-3 grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ">
      
      {individuals.map((individual) => (
        <div
          key={individual._id}
          className={`bg-white border rounded-2xl border-slate-700 transition-all duration-200 cursor-pointer group relative hover:shadow-lg hover:scale-105 hover:-translate-y-1 z-0 ${
            selectedIndividuals.includes(individual._id)
              ? 'ring-2 ring-indigo-500 border-indigo-500'
              : 'border-slate-200 hover:border-slate-300'
          }`}
          title={isModifierPressed 
            ? `${selectedIndividuals.includes(individual._id) ? 'Désélectionner' : 'Sélectionner'} ${individual.Name}` 
            : `Voir les détails de ${individual.Name}`
          }
        >
          {/* Checkbox de sélection */}
          <div className="absolute top-2 left-2 z-10">
            <input
              type="checkbox"
              checked={selectedIndividuals.includes(individual._id)}
              onChange={(e) => handleSelect(e, individual._id)}
              className="w-4 h-4 text-indigo-600 bg-white border-slate-300 rounded focus:ring-indigo-500 focus:ring-2"
            />
          </div>

          <div onClick={(e) => handleCardClick(e, individual._id)}>

            {/* Bouton de suppression */}
            <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => handleDelete(e, individual._id)}
                className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                title="Delete contact"
              >
                <i className="bi bi-x text-xs"></i>
              </button>
            </div>

            <div className="p-4">
              {/* Photo de profil */}
              <div className="flex justify-center mb-3">
                {individual.photo ? (
                  <img
                    src={individual.photo}
                    alt={individual.Name}
                    className="w-16 h-16 rounded-full object-cover border border-gray-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                {/* Avatar par défaut si pas de photo ou erreur de chargement */}
                <div
                  className={`w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg ${individual.photo ? 'hidden' : 'flex'}`}
                >
                  {getInitials(individual.Name)}
                </div>
              </div>

              {/* Nom avec logo LinkedIn */}
              <div className="text-center mb-2">
                <div className="flex items-center justify-center gap-1.5">
                  <h3 className="font-medium text-indigo-600 truncate text-sm m-0" title={individual.Name}>
                    {individual.Name}
                  </h3>
                  {individual.linkedinUrl && (
                    <a
                      href={individual.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 text-blue-600 hover:text-blue-800 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                      title="Voir le profil LinkedIn"
                    >
                      <i className="bi bi-linkedin text-sm"></i>
                    </a>
                  )}
                </div>
              </div>

              {/* Informations principales */}
              <div className="space-y-1.5 text-xs text-slate-600 mb-3">

                {/* JobTitle et Company sur la même ligne */}
                {(individual.jobTitle || individual.company) && (
                  <div className="flex items-center justify-center gap-1.5 text-xs">
                    {individual.jobTitle && (
                      <span className="font-semibold text-gray-900 truncate" title={individual.jobTitle}>
                        {individual.jobTitle.length > 18 ? individual.jobTitle.slice(0, 18) + "..." : individual.jobTitle}
                      </span>
                    )}
                    {individual.jobTitle && individual.company && (
                      <span className="text-gray-400">-</span>
                    )}
                    {individual.company && (
                      <span className="text-gray-600 truncate" title={individual.company}>
                        {individual.company.length > 15 ? individual.company.slice(0, 15) + "..." : individual.company}
                      </span>
                    )}
                  </div>
                )}

                {/* Ville */}
                {individual.city && (
                  <div className="flex items-center justify-center gap-1">
                    <i className="bi bi-geo-alt text-gray-400"></i>
                    <span className="truncate" title={individual.city}>
                      {individual.city.split(',')[0].trim()}
                    </span>
                  </div>
                )}

                {/* Formation */}
                {individual.formation && (
                  <div className="flex items-center justify-center gap-1">
                    <i className="bi bi-mortarboard text-gray-400"></i>
                    <span className="truncate" title={individual.formation}>
                      {individual.formation.length > 20 ? individual.formation.slice(0, 20) + "..." : individual.formation}
                    </span>
                  </div>
                )}

                {/* Téléphone */}
                {individual.phoneNumber && (
                  <div className="flex items-center justify-center gap-1">
                    <i className="bi bi-telephone-fill text-gray-500"></i>
                    <span className="truncate">
                      {formatPhoneNumber(individual.phoneNumber)}
                    </span>
                  </div>
                )}

                {/* Email pro */}
                {getAllEmails(individual.professionalEmail).length > 0 && (
                  <div className="flex items-center justify-center gap-1">
                    <i className="bi bi-envelope-fill text-blue-500"></i>
                    <span className="truncate" title={getFirstEmail(individual.professionalEmail)}>
                      {getFirstEmail(individual.professionalEmail).length > 22 ? getFirstEmail(individual.professionalEmail).slice(0, 22) + "..." : getFirstEmail(individual.professionalEmail)}
                    </span>
                  </div>
                )}

                {/* Email perso */}
                {getAllEmails(individual.personalEmail).length > 0 && (
                  <div className="flex items-center justify-center gap-1">
                    <i className="bi bi-envelope-fill text-green-500"></i>
                    <span className="truncate" title={getFirstEmail(individual.personalEmail)}>
                      {getFirstEmail(individual.personalEmail).length > 22 ? getFirstEmail(individual.personalEmail).slice(0, 22) + "..." : getFirstEmail(individual.personalEmail)}
                    </span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {individual.tags && individual.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center">
              {individual.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag._id}
                  className={`px-2 py-1 rounded-full text-xs font-medium  ${getTagStyle(tag)}`}
                  title={tag.name}
                >
                  {tag.name.length > 8 ? tag.name.slice(0, 8) + "..." : tag.name}
                </span>
              ))}
            </div>
          )}

              {/* Notation avec étoiles si présente */}
              {individual.score > 0 && (
                <div className="flex justify-center mt-2">
                  <div className="flex">
                    {[...Array(5)].map((_, index) => (
                      <i
                        key={index}
                        className={`bi bi-star${index < individual.score ? '-fill' : ''} text-xs ${
                          index < individual.score ? 'text-yellow-400' : 'text-slate-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Indicateur si dans un processus */}
            {individual.inProcess && (
              <div className="absolute bottom-2 right-2">
                <div className="w-2 h-2 bg-green-500 rounded-full" title="In recruitment process"></div>
              </div>
            )}

          </div>
        </div>
      ))}
    </div>

    {/* Confirmation Dialog */}
    <ConfirmationDialog
      isOpen={showDeleteDialog}
      onClose={() => {
        setShowDeleteDialog(false);
        setIndividualToDelete(null);
      }}
      onConfirm={confirmDelete}
      title="Supprimer le contact"
      message="Êtes-vous sûr de vouloir supprimer ce contact ? Cette action est irréversible."
      confirmText="Supprimer"
      cancelText="Annuler"
      isDangerous={true}
    />
    </div>
  );
};

export default IndividualsGrid;
export { default as IndividualsGrid } from './IndividualsGrid';